import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { TablePagination } from '@/components/ui/TablePagination';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import { customersApi } from '@/api/customers.api';
import { showApiError } from '@/utils/apiError';

interface OrgContact {
  id: number;
  customerId?: number;
  customerName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  jobTitle?: string;
  primaryContact?: boolean;
}

export function CrmContactsPage() {
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [contacts, setContacts] = useState<OrgContact[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await customersApi.listContacts({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setContacts(r.content);
      pagination.applyPageResponse(r);
    } catch (e) {
      showApiError(e, 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div>
      <PageHeader
        title="Contacts"
        description="Organization-wide customer contacts linked to Customer 360"
        breadcrumbs={[{ label: 'CRM', to: '/crm' }, { label: 'Contacts' }]}
      />
      <div className="card p-4">
        <div className="flex gap-2 mb-4">
          <input
            type="search"
            className="input flex-1 max-w-md"
            placeholder="Search name or email"
            value={pagination.search}
            onChange={(e) => {
              pagination.setSearch(e.target.value);
              pagination.resetPage();
            }}
            aria-label="Search contacts"
          />
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Email</th>
              <th className="p-2 text-left">Phone</th>
              <th className="p-2 text-left">Customer</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-b">
                <td className="p-2">
                  {[c.firstName, c.lastName].filter(Boolean).join(' ') || '—'}
                  {c.primaryContact && <span className="ml-1 text-xs text-primary-600">primary</span>}
                </td>
                <td className="p-2">{c.email ?? '—'}</td>
                <td className="p-2">{c.phone ?? '—'}</td>
                <td className="p-2">
                  {c.customerId ? (
                    <Link to={`/org/customers/${c.customerId}`} className="text-primary-600">
                      {c.customerName ?? `#${c.customerId}`}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
            {contacts.length === 0 && !loading && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-slate-500">
                  No contacts found
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={contacts.length > 0}
        />
      </div>
    </div>
  );
}
